import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import DataTable from "react-data-table-component";
import BASE_URL from "../../../../assets/global/url";
import { customStyles } from "../../../../assets/global/table-style";
import { useServerPagination } from "../../../../hooks/customHook/paginationHook/useServerPagination";
import { PaginationControls } from "../../../../hooks/customHook/paginationHook/usePagination";
import { format } from "date-fns";
import DateRangePicker from "../../../../components/DateRangePicker";
import { Link } from "react-router-dom";
import {
  formatCurrency,
  formatToTwoDecimal,
} from "../../../../utils/numberFormatter";

// Columns for Vendor List (Modal 1)
const columnVendorList = [
  {
    name: "Business Name",
    sortable: true,
    sortField: "company_name",
    selector: (row) => <div className="text-center">{row?.company_name}</div>,
  },
  {
    name: "Contact Person",
    sortable: true,
    sortField: "contact_person",
    selector: (row) => (
      <div className="text-center">
        {row?.fname} {row?.lname}
      </div>
    ),
  },
  {
    name: "Phone",
    sortable: true,
    sortField: "contact",
    selector: (row) => <div className="text-center">{row?.contact}</div>,
  },
  {
    name: "Email",
    sortable: true,
    sortField: "company_email",
    selector: (row) => <div>{row?.company_email}</div>,
  },
  {
    name: "Address",
    sortable: true,
    sortField: "company_address",
    selector: (row) => (
      <div className="text-center">{row?.company_address}</div>
    ),
  },
  {
    name: "Country",
    sortable: true,
    sortField: "company_country",
    selector: (row) => (
      <div className="text-center">{row?.company_country}</div>
    ),
  },
];

// Columns for Transaction List (Modal 2)
const columnTransactionList = [
  {
    name: "Transaction Date",
    sortable: true,
    sortField: "createdAt",
    selector: (row) =>
      row?.createdAt
        ? format(new Date(row?.createdAt), "MMM/dd/yyyy, hh:mm a")
        : "",
  },
  {
    name: "Transaction No.",
    sortable: true,
    sortField: "client_transaction_id",
    selector: (row) => (
      <div className="text-center">{row?.client_transaction_id}</div>
    ),
  },
  {
    name: "Discount",
    sortable: true,
    sortField: "discount_value",
    selector: (row) => <div className="text-center">{row?.discount_value}</div>,
  },
  {
    name: "Destination",
    sortable: true,
    sortField: "domestic_type",
    selector: (row) => (
      <div className="text-center">
        {row?.domestic_type === "local" ? "Local" : "Overseas"}
      </div>
    ),
  },
  {
    name: "Deadline",
    sortable: true,
    sortField: "due_date",
    selector: (row) =>
      row?.due_date ? format(new Date(row?.due_date), "MMM/dd/yyyy") : "",
  },
  {
    name: "Purchase Date",
    sortable: true,
    sortField: "purchaseDate",
    selector: (row) =>
      row?.purchaseDate
        ? format(new Date(row?.purchaseDate), "MMM/dd/yyyy")
        : "",
  },
  {
    name: "Date Approved",
    sortable: true,
    sortField: "date_approved",
    selector: (row) =>
      row?.date_approved
        ? format(new Date(row?.date_approved), "MMM/dd/yyyy, hh:mm a")
        : "",
  },
  {
    name: "Total Amount",
    sortable: true,
    sortField: "totalPrice",
    selector: (row) => (
      <div className="text-center fw-bold">
        {formatCurrency(row.totalPrice, row.currency.currency_name)}
      </div>
    ),
  },
  {
    name: "Status",
    sortable: true,
    sortField: "status",
    selector: (row) => (
      <div
        className="text-center fw-bold"
        style={{
          color:
            row?.status === "Paid"
              ? "green"
              : row?.status === "Approved"
                ? "blue"
                : "orange",
        }}
      >
        {row?.status}
      </div>
    ),
  },
];

const PayableReOpenModal = ({
  selectedCutOff,
  currencyId,
  handleDateRangeChange,
}) => {
  // Modal State
  const [firstModal, setFirstModal] = useState(false);
  const [secondModal, setSecondModal] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);

  // Search & Filter State
  const [searchTextVendor, setSearchTextVendor] = useState("");
  const [searchTextTransaction, setSearchTextTransaction] = useState("");
  const [filterColumn, setFilterColumn] = useState("all");
  const debounceTimer = useRef(null);

  // Selection State
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [toggledClearRows, setToggleClearRows] = useState(false);

  // Fetch Data into Pagination
  const vendorsWithPayableData = useServerPagination(
    `${BASE_URL}/payable/fetchVendorsWithPayables`,
    10,
    {
      startDate: selectedCutOff?.from,
      endDate: selectedCutOff?.to,
      currencyId,
      searchText: "",
      filterColumn: "all",
    },
  );

  const transactionsPagination = useServerPagination(
    `${BASE_URL}/payable/fetchSpecificVendorTransactions`,
    10,
    {
      vendorId: null,
      startDate: selectedCutOff?.from,
      endDate: selectedCutOff?.to,
      currencyId,
      searchText: "",
      filterColumn: "all",
    },
  );

  // use Effect Logic
  useEffect(() => {
    if (selectedCutOff?.from && selectedCutOff?.to) {
      vendorsWithPayableData.updateParams({
        startDate: selectedCutOff?.from,
        endDate: selectedCutOff?.to,
        currencyId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCutOff, currencyId]);

  useEffect(() => {
    if (selectedVendor && secondModal) {
      transactionsPagination.updateParams({
        startDate: selectedCutOff?.from,
        endDate: selectedCutOff?.to,
        currencyId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCutOff, currencyId, selectedVendor]);

  // Modal Handlers
  const handleFirstModalClose = () => {
    setFirstModal(false);
    setSearchTextVendor("");
    handleFilterColumnVendor("all");
  };

  const handleFirstModalShow = () => {
    setFirstModal(true);
    setSearchTextVendor("");
    handleFilterColumnVendor("all");
  };

  const handleRowClick = (row) => {
    setSelectedVendor(row);

    transactionsPagination.updateParams({
      vendorId: row.id,
      startDate: selectedCutOff?.from,
      endDate: selectedCutOff?.to,
      currencyId,
      searchText: "",
      filterColumn: "all",
    });

    setFirstModal(false);
    setSecondModal(true);
  };

  const handleBack = () => {
    setSecondModal(false);
    setFirstModal(true);
    setSearchTextTransaction("");
    setSelectedTransactions([]);
    setToggleClearRows((prev) => !prev);
  };

  const handleSecondModalClose = () => {
    setSecondModal(false);
    setSearchTextTransaction("");
    setSelectedTransactions([]);
    setToggleClearRows((prev) => !prev);
  };

  const handleRowSelected = ({ selectedRows }) => {
    setSelectedTransactions(selectedRows);
  };

  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  // --- VENDOR MODAL HANDLERS ---
  const handleSearchVendor = useCallback(
    (value) => {
      setSearchTextVendor(value);
      const raw = String(value).replace(/,/g, "");

      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        vendorsWithPayableData.updateParams({
          searchText: raw,
          filterColumn: filterColumn,
        });
      }, 500);
    },
    [filterColumn, vendorsWithPayableData],
  );

  const handleFilterColumnVendor = useCallback(
    (column) => {
      setFilterColumn(column);
      vendorsWithPayableData.updateParams({
        filterColumn: column,
        searchText: String(searchTextVendor).replace(/,/g, ""),
      });
    },
    [searchTextVendor, vendorsWithPayableData],
  );

  const handleSortVendor = useCallback(
    (column, sortDirection) => {
      vendorsWithPayableData.updateParams({
        sortColumn: column.sortField,
        sortDirection: sortDirection, // 'asc' or 'desc'
      });
    },
    [vendorsWithPayableData],
  );

  // --- TRANSACTION MODAL HANDLERS ---
  const handleSearchTransaction = useCallback(
    (value) => {
      setSearchTextTransaction(value);

      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        transactionsPagination.updateParams({
          searchText: value,
          filterColumn: filterColumn,
        });
      }, 500);
    },
    [filterColumn, transactionsPagination],
  );

  const handleFilterColumnTransaction = useCallback(
    (column) => {
      setFilterColumn(column);
      transactionsPagination.updateParams({
        filterColumn: column,
        searchText: searchTextTransaction,
      });
    },
    [searchTextTransaction, transactionsPagination],
  );

  const handleSortTransaction = useCallback(
    (column, sortDirection) => {
      transactionsPagination.updateParams({
        sortColumn: column.sortField,
        sortDirection: sortDirection,
      });
    },
    [transactionsPagination],
  );

  return (
    <>
      <Button
        variant="primary"
        className="d-flex flex-row align-items-center pt-2 px-3 me-2"
        onClick={handleFirstModalShow}
      >
        Re-Open
      </Button>

      {/* 1st MODAL: Vendor List */}
      <Modal
        show={firstModal}
        onHide={handleFirstModalClose}
        backdrop="static"
        size="xl"
        centered
        scrollable={true}
        dialogClassName="payable-reopen-modal-size"
      >
        <Modal.Header closeButton>
          <Modal.Title>Vendor List</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex flex-wrap align-items-end mb-3 gap-2">
            {/* Search Input for Vendor*/}
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                placeholder="Search"
                value={searchTextVendor}
                onChange={(e) => handleSearchVendor(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-outline-secondary dropdown-toggle dropdown-toggle-split"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i className="fa-solid fa-sliders"></i>
                <span className="visually-hidden">Toggle Dropdown</span>
              </button>
              <ul className="dropdown-menu dropdown-menu-end">
                <li>
                  <button
                    className={`dropdown-item ${
                      filterColumn === "all" ? "active" : ""
                    }`}
                    onClick={() => {
                      handleFilterColumnVendor("all");
                    }}
                  >
                    All
                  </button>
                </li>
                <li>
                  <button
                    className={`dropdown-item ${
                      filterColumn === "company_name" ? "active" : ""
                    }`}
                    onClick={() => {
                      handleFilterColumnVendor("company_name");
                    }}
                  >
                    Business Name
                  </button>
                </li>
                <li>
                  <button
                    className={`dropdown-item ${
                      filterColumn === "contact_person" ? "active" : ""
                    }`}
                    onClick={() => {
                      handleFilterColumnVendor("contact_person");
                    }}
                  >
                    Contact Person
                  </button>
                </li>
                <li>
                  <button
                    className={`dropdown-item ${
                      filterColumn === "phone" ? "active" : ""
                    }`}
                    onClick={() => {
                      handleFilterColumnVendor("phone");
                    }}
                  >
                    Phone
                  </button>
                </li>
                <li>
                  <button
                    className={`dropdown-item ${
                      filterColumn === "company_email" ? "active" : ""
                    }`}
                    onClick={() => {
                      handleFilterColumnVendor("company_email");
                    }}
                  >
                    Email
                  </button>
                </li>
                <li>
                  <button
                    className={`dropdown-item ${
                      filterColumn === "company_address" ? "active" : ""
                    }`}
                    onClick={() => {
                      handleFilterColumnVendor("company_address");
                    }}
                  >
                    Address
                  </button>
                </li>
                <li>
                  <button
                    className={`dropdown-item ${
                      filterColumn === "company_country" ? "active" : ""
                    }`}
                    onClick={() => {
                      handleFilterColumnVendor("company_country");
                    }}
                  >
                    Country
                  </button>
                </li>
              </ul>
            </div>
          </div>
          <div className="data-table-cell-width">
            <DataTable
              columns={columnVendorList}
              data={vendorsWithPayableData.data}
              highlightOnHover
              pointerOnHover
              className="dataTable"
              customStyles={customStyles}
              onRowClicked={handleRowClick}
              sortServer={true}
              onSort={handleSortVendor}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="w-100 d-flex justify-content-between align-items-center">
            <PaginationControls {...vendorsWithPayableData} />
            <Button variant="secondary" onClick={handleFirstModalClose}>
              Cancel
            </Button>
            {/* Not needed. Commenting for now. if flow changes might be useful */}
            {/* <Button variant="primary" onClick={handleNext}>
                Next
              </Button> */}
          </div>
        </Modal.Footer>
      </Modal>

      {/* 2nd MODAL: Transaction List */}
      <Modal
        show={secondModal}
        onHide={handleSecondModalClose}
        backdrop="static"
        size="xl"
        centered
        scrollable={true}
        dialogClassName="payable-reopen-modal-size"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Transactions List:{" "}
            <span className="text-primary">{selectedVendor?.company_name}</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Header Controls: Date Picker & Search */}
          <div className="d-flex flex-wrap justify-content-between align-items-end mb-3 gap-2">
            <div style={{ minWidth: "250px" }}>
              <DateRangePicker
                startDate={selectedCutOff?.from}
                endDate={selectedCutOff?.to}
                onDateRangeChange={handleDateRangeChange}
                label="Transaction Date"
              />
            </div>

            {/* Search Input for Transactions */}
            <div className="input-group" style={{ maxWidth: "400px" }}>
              <input
                type="text"
                className="form-control"
                placeholder="Search"
                value={searchTextTransaction}
                onChange={(e) => handleSearchTransaction(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-outline-secondary dropdown-toggle dropdown-toggle-split"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i className="fa-solid fa-sliders"></i>
                <span className="visually-hidden">Toggle Dropdown</span>
              </button>
              <ul className="dropdown-menu dropdown-menu-end">
                <li>
                  <button
                    className={`dropdown-item ${
                      filterColumn === "all" ? "active" : ""
                    }`}
                    onClick={() => {
                      handleFilterColumnTransaction("all");
                    }}
                  >
                    All
                  </button>
                </li>
                <li>
                  <button
                    className={`dropdown-item ${
                      filterColumn === "createdAt" ? "active" : ""
                    }`}
                    onClick={() => {
                      handleFilterColumnTransaction("createdAt");
                    }}
                  >
                    Transaction Date
                  </button>
                </li>
                <li>
                  <button
                    className={`dropdown-item ${
                      filterColumn === "client_transaction_id" ? "active" : ""
                    }`}
                    onClick={() => {
                      handleFilterColumnTransaction("client_transaction_id");
                    }}
                  >
                    Transaction No.
                  </button>
                </li>
                <li>
                  <button
                    className={`dropdown-item ${filterColumn === "product_name" ? "active" : ""}`}
                    onClick={() =>
                      handleFilterColumnTransaction("product_name")
                    }
                  >
                    Product Name
                  </button>
                </li>
                <li>
                  <button
                    className={`dropdown-item ${filterColumn === "product_code" ? "active" : ""}`}
                    onClick={() =>
                      handleFilterColumnTransaction("product_code")
                    }
                  >
                    Product Code
                  </button>
                </li>
                <li>
                  <button
                    className={`dropdown-item ${
                      filterColumn === "discount_value" ? "active" : ""
                    }`}
                    onClick={() => {
                      handleFilterColumnTransaction("discount_value");
                    }}
                  >
                    Discount
                  </button>
                </li>
                <li>
                  <button
                    className={`dropdown-item ${
                      filterColumn === "domestic_type" ? "active" : ""
                    }`}
                    onClick={() => {
                      handleFilterColumnTransaction("domestic_type");
                    }}
                  >
                    Destination
                  </button>
                </li>
                <li>
                  <button
                    className={`dropdown-item ${
                      filterColumn === "due_date" ? "active" : ""
                    }`}
                    onClick={() => {
                      handleFilterColumnTransaction("due_date");
                    }}
                  >
                    Deadline
                  </button>
                </li>
                <li>
                  <button
                    className={`dropdown-item ${
                      filterColumn === "purchaseDate" ? "active" : ""
                    }`}
                    onClick={() => {
                      handleFilterColumnTransaction("purchaseDate");
                    }}
                  >
                    Purchase Date
                  </button>
                </li>
                <li>
                  <button
                    className={`dropdown-item ${
                      filterColumn === "date_approved" ? "active" : ""
                    }`}
                    onClick={() => {
                      handleFilterColumnTransaction("date_approved");
                    }}
                  >
                    Date Approved
                  </button>
                </li>
                <li>
                  <button
                    className={`dropdown-item ${
                      filterColumn === "totalPrice" ? "active" : ""
                    }`}
                    onClick={() => {
                      handleFilterColumnTransaction("totalPrice");
                    }}
                  >
                    Total Amount
                  </button>
                </li>
                <li>
                  <button
                    className={`dropdown-item ${
                      filterColumn === "status" ? "active" : ""
                    }`}
                    onClick={() => {
                      handleFilterColumnTransaction("status");
                    }}
                  >
                    Status
                  </button>
                </li>
              </ul>
            </div>
          </div>

          {/* Data Table Transaction Lists */}
          <div className="data-table-cell-width">
            <DataTable
              columns={columnTransactionList}
              data={transactionsPagination.data || []}
              highlightOnHover
              className="dataTable"
              customStyles={customStyles}
              selectableRows
              onSelectedRowsChange={handleRowSelected}
              clearSelectedRows={toggledClearRows}
              sortServer={true}
              onSort={handleSortTransaction}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="w-100 d-flex justify-content-between align-items-center">
            <PaginationControls {...transactionsPagination} />
            <div>
              <Button variant="secondary" onClick={handleBack} className="me-2">
                Back
              </Button>
              <Link
                to="/purchases/transaction-list"
                state={{
                  transactions: selectedTransactions.map((t) => t.id),
                  vendor: selectedVendor,
                  selectedCutOff: selectedCutOff,
                }}
              >
                <Button
                  variant="primary"
                  disabled={selectedTransactions.length === 0}
                >
                  Proceed
                </Button>
              </Link>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PayableReOpenModal;
